import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { sqlImportRowKeys, type SqlImportRow } from './entity';

/**
 * The runs of one import, newest first as the backend orders them. The list is
 * capped server-side (100 by default), so this is the whole history the product
 * shows, not a page of it.
 *
 * The helper lives under `sqlImports` rather than `sqlImportRows` because the
 * route is tagged `sql_imports` in the spec.
 */
export async function getSqlImportRows(workspaceId: number, importId: string): Promise<SqlImportRow[]> {
  const data = await hydraUiApi.sqlImports.getSqlImportRows({
    params: { path: { environment_id: String(workspaceId) }, query: { sql_import_id: importId } },
  });
  return data.sql_import_rows;
}

/** Always fresh: a run's status moves server-side between reads. */
export const getSqlImportRowsQueryOptions = (workspaceId: number, importId: string) =>
  queryOptions({
    queryKey: sqlImportRowKeys.list(workspaceId, importId),
    queryFn: () => getSqlImportRows(workspaceId, importId),
    staleTime: 0,
  });

interface UseSqlImportRowsOptions {
  importId: string;
  /**
   * Use this id instead of reading the URL. Pages that render inside a Suspense
   * boundary can mount while the URL is still mid-redirect, so a caller that
   * already resolved the workspace passes it in.
   */
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getSqlImportRowsQueryOptions>;
}

/** @public */
export const useSqlImportRows = ({
  importId,
  workspaceId: overrideWorkspaceId,
  queryConfig,
}: UseSqlImportRowsOptions) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useSuspenseQuery({ ...getSqlImportRowsQueryOptions(workspaceId, importId), ...queryConfig });
};
